export const LINKFLARE_APP_STORE_ANNUAL_PRODUCT_ID =
  import.meta.env.VITE_APP_STORE_ANNUAL_PRODUCT_ID || 'io.linkflare.app.pro.annual'

export const NATIVE_STOREKIT_PRODUCT_LOAD_TIMEOUT_MS = 8000

export function getNativeStoreKitBridge() {
  if (typeof window === 'undefined') return null
  return window.linkflareStoreKit || null
}

export function isNativeStoreKitAvailable(bridge = getNativeStoreKitBridge()) {
  return !!(
    bridge &&
    bridge.isAvailable === true &&
    typeof bridge.getProducts === 'function' &&
    typeof bridge.purchase === 'function' &&
    typeof bridge.restorePurchases === 'function'
  )
}

/**
 * True when the page is running inside the macOS App Store wrapper.
 *
 * The wrapper sets this flag in a document-start user script, independently of
 * `window.linkflareStoreKit`. Purchase surfaces key off the BRIDGE to decide
 * whether StoreKit works, but they must key off THIS to decide whether a web
 * checkout is allowed at all: if the bridge ever fails to inject, the app is
 * still an App Store app, and showing a Stripe form there is an external
 * purchase path (App Review guideline 3.1.1).
 */
export function isMacAppShell() {
  if (typeof window === 'undefined') return false
  return window.__LINKFLARE_MAC_APP === true
}

export function getNativeStoreKitProductIds(bridge = getNativeStoreKitBridge()) {
  if (Array.isArray(bridge?.defaultProductIds) && bridge.defaultProductIds.length > 0) {
    return bridge.defaultProductIds
  }
  return [LINKFLARE_APP_STORE_ANNUAL_PRODUCT_ID]
}

function withNativeStoreKitTimeout(promise, timeoutMs, message) {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return promise

  let timeoutId
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs)
  })

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId))
}

export async function loadNativeStoreKitProducts({
  bridge = getNativeStoreKitBridge(),
  productIds = getNativeStoreKitProductIds(bridge),
  timeoutMs = NATIVE_STOREKIT_PRODUCT_LOAD_TIMEOUT_MS
} = {}) {
  if (!isNativeStoreKitAvailable(bridge)) return []
  const response = await withNativeStoreKitTimeout(
    bridge.getProducts(productIds),
    timeoutMs,
    'App Store product lookup timed out.'
  )
  return Array.isArray(response?.products) ? response.products : []
}

export function buildAppStoreTransactionPayload(purchaseResult) {
  const transaction = purchaseResult?.transaction || purchaseResult
  if (!transaction || typeof transaction !== 'object') {
    throw new Error('Missing App Store transaction.')
  }

  const productId = transaction.productId || transaction.product_id
  const transactionId = transaction.transactionId || transaction.transaction_id || transaction.id
  const originalTransactionId =
    transaction.originalTransactionId ||
    transaction.original_transaction_id ||
    transaction.originalID ||
    transaction.originalId
  const signedTransactionInfo =
    transaction.signedTransactionInfo ||
    transaction.signed_transaction_info ||
    transaction.jwsRepresentation

  if (!productId || !transactionId || !originalTransactionId || !signedTransactionInfo) {
    throw new Error('Incomplete App Store transaction.')
  }

  return {
    product_id: productId,
    transaction_id: String(transactionId),
    original_transaction_id: String(originalTransactionId),
    signed_transaction_info: signedTransactionInfo
  }
}

async function verifyNativeTransaction({ bridge, payload, token, verifyTransaction }) {
  if (typeof bridge?.verifyTransaction === 'function') {
    return bridge.verifyTransaction(payload, token)
  }
  if (typeof verifyTransaction === 'function') {
    return verifyTransaction(payload, token)
  }
  throw new Error('App Store transaction verification is not configured.')
}

export async function purchaseNativeSubscription({
  bridge = getNativeStoreKitBridge(),
  productId = getNativeStoreKitProductIds(bridge)[0],
  token = null,
  verifyTransaction
} = {}) {
  if (!isNativeStoreKitAvailable(bridge)) {
    throw new Error('StoreKit is not available in this app.')
  }

  const purchaseResult = await bridge.purchase(productId)
  if (purchaseResult?.status === 'pending' || purchaseResult?.status === 'cancelled') {
    return { status: purchaseResult.status, purchaseResult }
  }

  const payload = buildAppStoreTransactionPayload(purchaseResult)
  const verification = await verifyNativeTransaction({ bridge, payload, token, verifyTransaction })

  if (typeof bridge.finishTransaction === 'function') {
    await bridge.finishTransaction(payload.transaction_id)
  }

  return {
    status: 'verified',
    payload,
    purchaseResult,
    verification
  }
}

export async function restoreNativeSubscription({
  bridge = getNativeStoreKitBridge(),
  token = null,
  verifyTransaction
} = {}) {
  if (!isNativeStoreKitAvailable(bridge)) {
    throw new Error('StoreKit is not available in this app.')
  }

  const restoreResult = await bridge.restorePurchases()
  const entitlements = Array.isArray(restoreResult?.entitlements) ? restoreResult.entitlements : []
  const verifications = []

  for (const entitlement of entitlements) {
    const payload = buildAppStoreTransactionPayload(entitlement)
    verifications.push(await verifyNativeTransaction({ bridge, payload, token, verifyTransaction }))
  }

  return {
    status: entitlements.length > 0 ? 'restored' : 'empty',
    entitlements,
    verifications
  }
}
